import { Alert, Badge, BadgeColor, Button } from '@odin-labs/components';
import React from 'react';
import {
  DraftTag,
  Enforcement,
  Entry,
  entryFacesSameZone,
  entryJoinLabel,
  EntrySide,
  isDuplicateName,
  Layout,
  readerAssignmentName,
} from 'siteLayout';
import { draftTagBadge } from 'containers/jobsiteConfiguration/tabs/siteLayout/labels';
import { EntrySideCard, SideReader } from './EntrySideCard';
import { NameField } from './NameField';

export type EntryEditorProps = {
  layout: Layout;
  entry: Entry;
  tag: DraftTag;
  readOnly: boolean;
  editingDraft: boolean;
  /** Each side's Avigilon entry; absent where the revision shown says nothing about them. */
  avigilonStatus?: React.ReactNode;
  /** The site's reader devices only, keyed by reader device id; never populated from layout history. */
  readersById: Map<string, SideReader>;
  pickerSideId: string | null;
  onPickerSideChange: (sideId: string | null) => void;
  onRename: (name: string) => Promise<boolean> | void;
  onSetFaces: (side: EntrySide, value: string) => void;
  onSetEnforcement: (side: EntrySide, enforcement: Enforcement) => void;
  onPlaceReader: (side: EntrySide, readerDeviceId: string) => void;
  onRemoveReader: (readerDeviceId: string) => void;
  onRemove: () => void;
};

const classes = {
  panel: 'odin-flex odin-flex-col odin-gap-y-5',
  heading: 'odin-flex odin-items-start odin-justify-between odin-gap-3',
  kicker:
    'odin-text-xs odin-font-semibold odin-uppercase odin-tracking-wide odin-text-gray-500',
  title:
    'odin-text-2xl odin-font-semibold odin-tracking-tight odin-text-gray-900',
  sides: 'odin-grid odin-grid-cols-1 odin-gap-4 lg:odin-grid-cols-2',
  join: 'odin-rounded-md odin-bg-gray-50 odin-px-3 odin-py-2 odin-font-mono odin-text-xs odin-text-gray-600',
  footer:
    'odin-flex odin-items-center odin-gap-3 odin-border-t odin-border-gray-200 odin-pt-5',
  note: 'odin-text-xs odin-text-gray-500',
};

export const entryNameError = (layout: Layout, entry: Entry): string | null => {
  if (!entry.name.trim()) return 'An entry needs a name.';
  if (isDuplicateName(layout.entries, entry)) {
    return `An entry named ${entry.name.trim()} already exists in this layout.`;
  }
  return null;
};

/** The selected entry: its name, its two sides with their readers, and removal from the draft. */
export function EntryEditor({
  layout,
  entry,
  tag,
  readOnly,
  editingDraft,
  avigilonStatus,
  readersById,
  pickerSideId,
  onPickerSideChange,
  onRename,
  onSetFaces,
  onSetEnforcement,
  onPlaceReader,
  onRemoveReader,
  onRemove,
}: EntryEditorProps): React.ReactElement {
  const badge = draftTagBadge(tag);
  const placedIds = new Set(
    layout.readerAssignments.map((assignment) => assignment.readerDeviceId),
  );
  const unplacedPool = [...readersById.values()].filter(
    (reader) => !reader.missing && !placedIds.has(reader.id),
  );
  const readersInto = (side: EntrySide): SideReader[] =>
    layout.readerAssignments
      .filter((assignment) => assignment.admitsIntoSideId === side.id)
      .map((assignment) => {
        const siteReader = readersById.get(assignment.readerDeviceId);
        return {
          id: assignment.readerDeviceId,
          name: readerAssignmentName(assignment),
          // The layout API names the reader; the site's readers say where it
          // is wired and whether the provider still lists it.
          portLabel:
            siteReader?.portLabel ?? 'Controller and port details unavailable',
          missing: siteReader?.missing,
          notConnected: siteReader?.notConnected,
        };
      });
  const hasReaders = layout.readerAssignments.some(
    (assignment) => assignment.entryId === entry.id,
  );

  return (
    <div className={classes.panel}>
      <div className={classes.heading}>
        <div>
          <p className={classes.kicker}>Entry</p>
          <h2 className={classes.title}>{entry.name || 'Unnamed entry'}</h2>
        </div>
        {badge && <Badge text={badge.text} color={badge.color as BadgeColor} />}
      </div>

      <NameField
        value={entry.name}
        placeholder="The identifier an installer would use: North Gate, Door 14"
        error={entryNameError(layout, entry)}
        disabled={readOnly}
        onCommit={onRename}
      />

      <div className={classes.sides}>
        {[entry.sideA, entry.sideB].map((side) => (
          <EntrySideCard
            key={side.id}
            layout={layout}
            entry={entry}
            side={side}
            readers={readersInto(side)}
            unplacedPool={unplacedPool}
            readOnly={readOnly}
            editingDraft={editingDraft}
            pickerOpen={pickerSideId === side.id}
            onOpenPicker={() => onPickerSideChange(side.id)}
            onClosePicker={() => onPickerSideChange(null)}
            onSetFaces={(value) => onSetFaces(side, value)}
            onSetEnforcement={(enforcement) =>
              onSetEnforcement(side, enforcement)
            }
            onPlaceReader={(readerDeviceId) =>
              onPlaceReader(side, readerDeviceId)
            }
            onRemoveReader={onRemoveReader}
          />
        ))}
      </div>

      <p className={classes.join}>
        {entry.name || 'Entry'}: {entryJoinLabel(layout, entry)}
      </p>
      {entryFacesSameZone(entry) && (
        <Alert
          type="danger"
          text="Both sides face the same zone. Change one side so the entry joins two different zones, or a zone and Outside."
        />
      )}

      {avigilonStatus}

      {editingDraft && (
        <div className={classes.footer}>
          <Button
            text="Remove entry from draft"
            size="sm"
            theme="danger"
            disabled={readOnly}
            onClick={onRemove}
          />
          {hasReaders && (
            <span className={classes.note}>
              Its reader placements are removed from this draft.
            </span>
          )}
        </div>
      )}
    </div>
  );
}
